'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * Thin top progress bar shown during client-side navigation.
 * No page-level skeleton flash — just a clean bar like YouTube/GitHub.
 */
export function NavigationProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevPathRef = useRef(pathname + searchParams.toString());

  // Detect route change start (via link click → React transition)
  useEffect(() => {
    const currentKey = pathname + searchParams.toString();

    if (prevPathRef.current !== currentKey) {
      // Route changed — complete the bar
      prevPathRef.current = currentKey;
      complete();
    }
  }, [pathname, searchParams]);

  function start() {
    // Clear any existing timers
    if (timerRef.current) clearInterval(timerRef.current);
    if (completeTimerRef.current) clearTimeout(completeTimerRef.current);

    setProgress(5);
    setVisible(true);

    // Increment progress gradually — slows down near 90%
    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(timerRef.current!);
          return 90;
        }
        // Faster at start, slower near end
        const increment = prev < 30 ? 8 : prev < 60 ? 4 : 1.5;
        return Math.min(prev + increment, 90);
      });
    }, 100);
  }

  function complete() {
    if (timerRef.current) clearInterval(timerRef.current);
    setProgress(100);

    completeTimerRef.current = setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 300);
  }

  // Intercept all link clicks to start the bar
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = (e.target as HTMLElement).closest('a');
      if (!target) return;

      const href = target.getAttribute('href');
      if (!href) return;

      // Only internal navigation (not anchors, external links, or same page)
      const isInternal = href.startsWith('/') && !href.startsWith('//');
      const isSamePage = href === window.location.pathname + window.location.search;
      const isNewTab = target.target === '_blank';

      if (isInternal && !isSamePage && !isNewTab) {
        start();
      }
    }

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
    };
  }, []);

  if (!visible && progress === 0) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-[2px] pointer-events-none"
      aria-hidden="true"
    >
      <div
        className="h-full shadow-[0_0_8px_2px_rgba(192,32,41,0.6)] transition-all ease-out"
        style={{
          backgroundColor: '#c02029',
          width: `${progress}%`,
          transitionDuration: progress === 100 ? '200ms' : '100ms',
          opacity: visible ? 1 : 0,
          transition: `width ${progress === 100 ? '200ms' : '100ms'} ease-out, opacity 300ms ease`,
        }}
      />
    </div>
  );
}
